import { buildDaysheetDoc } from './daysheet';
import { crewRowsFromAssignments, personDisplayName, personOfficialName } from './people';
import type { DaysheetLayout } from './daysheetLayout';
import { mergeTourBookLayouts, createDefaultTourBookLayout, type TourBookLayout, type TourBookSectionId, tourbookTableWidths, TOURBOOK_TABLE_COLUMNS, tourbookTableColumns } from './tourbookLayout';
import { generatePdfBlobViaServer } from './pdf';
import { t, pdfLocale } from './pdfI18n';
import { DEFAULT_TOURBOOK_LAYOUT_TEXTS } from './tourbookLayout';

/**
 * Tour Book PDF builder (2026-08-11).
 *
 * A single document that gathers every show of a tour:
 *   1. an introduction page (tour + band),
 *   2. the tour's technical info,
 *   3. each show's daysheet in chronological order,
 *   4. a footer note at the end of each daysheet telling where the NEXT date of
 *      the tour is (except for the last date).
 *
 * The document's high-level sections (intro / technical-overview / daysheets /
 * next-note) are driven by a `tourbookLayout` (src/lib/tourbookLayout.ts). Each
 * embedded daysheet is additionally laid out with the band's `daysheetLayout`
 * (src/lib/daysheetLayout.ts).
 */

interface TourBookInput {
  tour: { id: string | number; name: string; notes?: string | null };
  band?: { id: string; name: string; slug?: string } | null;
  days: Array<{
    show: any;
    roadmap: any[];
    rooming: any[];
    accommodations: any[];
    guests: any[];
    contacts: any[];
    distances?: Array<{ from_label: string; to_label: string; mode?: string; distance?: string; duration?: string }>;
    /** Le registre de l'équipe tel que l'API du tour book le renvoie (id, name,
     *  nickname, real_name, last_name, category, role) : nomme BAND AND CREW et
     *  fournit l'identité PASSEPORT des rooming lists et des vols. */
    team?: Array<{ id: string | number; name?: string; nickname?: string; real_name?: string; last_name?: string; role?: string; tags?: string[]; category?: string }>;
    /** « People on this show » déjà résolu par le serveur (nom affiché + poste PAR BAND),
     *  même règle que la page show. Absent ⇒ repli sur le rooming. */
    people?: Array<{ name: string; position?: string }>;
    /** Photo « document » de la salle de ce jour (URL + nom), renvoyée par l'API. */
    venue_photo?: { url?: string; name?: string; caption?: string } | null;
  }>;
  /** Optional Tour Book layout (its own sections/texts/style). */
  layout?: TourBookLayout | null;
  /** Optional daysheet layout applied to each embedded daysheet. */
  daysheetLayout?: DaysheetLayout | null;
  /** Photos de salle déjà résolues en data URL (clé = URL renvoyée par l'API).
   *  Le tour book ne peut pas les résoudre lui-même : pdfmake n'embarque que des
   *  data URLs PNG/JPEG, et la conversion a besoin du navigateur. */
  venuePhotoData?: Record<string, string>;
}

function fmtDate(d: string | undefined): string {
  if (!d) return 'TBD';
  try {
    return new Date(d).toLocaleDateString(pdfLocale(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return d;
  }
}

export function buildTourBookDoc(input: TourBookInput): any {
  const { tour, band, days, layout, daysheetLayout, venuePhotoData } = input;
  // Effective tour-book layout (merged over the defaults).
  const L = mergeTourBookLayouts(createDefaultTourBookLayout(), layout ?? null);
  const S = L.style;
  const T = L.texts;
  // Translate the default fixed texts (admin overrides preserved).
  if (T.titleSuffix === DEFAULT_TOURBOOK_LAYOUT_TEXTS.titleSuffix) T.titleSuffix = t('pdf.tb.titleSuffix');
  if (T.nextLabel === DEFAULT_TOURBOOK_LAYOUT_TEXTS.nextLabel) T.nextLabel = t('pdf.tb.nextLabel');
  const sec = (id: TourBookSectionId): boolean =>
    L.sections.find((s) => s.id === id)?.enabled ?? true;

  const content: any[] = [];

  // Field helpers: spacer gap + free-text paragraph (as in the rider/daysheet).
  const isSpacerField = (fid: string): boolean => fid === 'spacer' || fid.endsWith('.spacer');
  const isTextField = (fid: string): boolean => fid === 'text' || fid.endsWith('.text');
  const spacerNode = (size: number): any => ({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 0, y2: 0, lineWidth: 0 }],
    margin: [0, Math.max(0, size) / 2, 0, Math.max(0, size) / 2],
  });
  const textNode = (f: any): any | null => {
    const c = (f.textContent ?? '').trim();
    if (!c) return null;
    return {
      text: c,
      ...(f.bold ? { bold: true } : {}),
      ...(f.italic ? { italics: true } : {}),
      ...(f.fontSize ? { fontSize: f.fontSize } : {}),
      ...(f.align && f.align !== 'left' ? { alignment: f.align } : {}),
      margin: [0, 4, 0, 4],
    };
  };
  // Colonnes des tableaux : ordre, visibilité, libellé et largeur viennent du
  // layout (modèle rider). Les builders produisent toutes les colonnes.
  const applyColumnLayout = (sid: TourBookSectionId, engine: any[]): any[] => {
    const defs = TOURBOOK_TABLE_COLUMNS[sid] || [];
    if (!defs.length) return engine;
    const sec = L.sections.find((x) => x.id === sid);
    const cols = tourbookTableColumns(sec, sid);
    const indexOf = new Map(defs.map((d, i) => [d.id, i]));
    const total = cols.reduce((a, c) => a + c.weight, 0) || 1;
    const widths = cols.map((c) => `${((c.weight / total) * 100).toFixed(2)}%`);
    return engine.flatMap((node) => {
      if (!node || !node.table) return [node];
      if (cols.length === 0) return [];
      const body: any[][] = Array.isArray(node.table.body) ? node.table.body : [];
      if (!body.length) return [node];
      const next = body.map((row, r) => {
        if (r === 0) {
          return cols.map((c) => {
            const idx = indexOf.get(c.id);
            const base = idx != null ? row[idx] : null;
            return { ...(base || {}), text: c.labelOverride || (base && base.text) || t(c.labelKey), bold: true };
          });
        }
        return cols.map((c) => {
          const idx = indexOf.get(c.id);
          return idx != null ? row[idx] : { text: '' };
        });
      });
      return [{ ...node, table: { ...node.table, widths, body: next } }];
    });
  };

  const emitSection = (id: TourBookSectionId, engineIn: any[]): any[] => {
    const engine = applyColumnLayout(id, engineIn);
    const sec = L.sections.find((s) => s.id === id);
    const fields = sec?.fields && sec.fields.length > 0 ? sec.fields : null;
    let out: any[] = fields ? [] : engine;
    if (fields) {
      let engineEmitted = false;
      for (const f of fields) {
        if (!f.enabled) continue;
        if (isSpacerField(f.id)) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
        if (isTextField(f.id)) { const n = textNode(f); if (n) out.push(n); continue; }
        if (!engineEmitted && engine.length > 0) { out.push(...engine); engineEmitted = true; }
      }
      if (!engineEmitted && engine.length > 0) out.push(...engine);
    }
    // pageBreakBefore → start this section on a new page (attach to first node).
    if (sec?.pageBreakBefore && out.length) {
      const first = { ...out[0] };
      if (first.text === '' && first.pageBreak === 'before') out[0] = first;
      else out[0] = { pageBreak: 'before', ...first };
    }
    return out;
  };

  // Largeurs de colonnes éditables (éditeur de layout), sinon défauts moteur.
  const colWidths = (id: TourBookSectionId): string[] =>
    tourbookTableWidths(id, L.sections.find((x) => x.id === id)?.columnWidths);

  // 1. Introduction page (tour book section 'intro').
  const buildIntro = (): any[] => {
    const out: any[] = [];
    out.push({ text: `${band?.name || 'Band'} — ${T.titleSuffix}`, fontSize: S.titleFontSize, bold: true, color: S.titleColor, alignment: 'center', margin: [0, 0, 0, 8] });
    out.push({ text: tour.name, fontSize: S.subtitleFontSize, alignment: 'center', color: S.subtitleColor, margin: [0, 0, 0, 12] });
    if (tour.notes) out.push({ text: tour.notes, fontSize: S.bodyFontSize, alignment: 'center', color: '#666', margin: [0, 0, 0, 20] });
    out.push({
      table: {
        widths: colWidths('intro'),
        body: [
          [ { text: t('pdf.tb.dates'), bold: true }, { text: String(days.length) } ],
          [ { text: t('pdf.tb.firstDate'), bold: true }, { text: days[0] ? fmtDate(days[0].show.date) : '—' } ],
          [ { text: t('pdf.tb.lastDate'), bold: true }, { text: days[days.length - 1] ? fmtDate(days[days.length - 1].show.date) : '—' } ],
        ],
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 12],
    });
    out.push({ text: '\n', margin: [0, 0, 0, 0] });
    return out;
  };

  // 2. Technical info of the tour (per-show tech essentials, condensed).
  const buildTechOverview = (): any[] => {
    const out: any[] = [];
    out.push({ text: t('pdf.tb.techOverview'), fontSize: S.headingFontSize, bold: true, color: S.headingColor, margin: [0, 12, 0, 6] });
    const techRows = days.map((d) => {
      const s = d.show || {};
      return [
        { text: fmtDate(s.date) },
        { text: s.name || '' },
        { text: [s.stage_name, s.stage_size].filter(Boolean).join(' · ') },
        { text: s.duration_minutes ? t('pdf.tb.min', { count: s.duration_minutes }) : '' },
      ];
    });
    out.push({
      table: {
        widths: colWidths('technical-overview'),
        body: [
          [ { text: t('pdf.tb.col.date'), bold: true }, { text: t('pdf.tb.col.show'), bold: true }, { text: t('pdf.tb.col.stage'), bold: true }, { text: t('pdf.tb.col.length'), bold: true } ],
          ...techRows,
        ],
      },
      layout: 'lightHorizontalLines',
      margin: [0, 0, 0, 16],
    });
    return out;
  };

  if (sec('intro')) content.push(...emitSection('intro', buildIntro()));
  if (sec('technical-overview')) content.push(...emitSection('technical-overview', buildTechOverview()));

  // 3. Each daysheet in chronological order, page-break before each.
  if (sec('daysheets')) {
    days.forEach((day, idx) => {
      const next = days[idx + 1];
      const dayDoc: any = buildDaysheetDoc({
        show: day.show,
        roadmap: day.roadmap,
        guests: day.guests,
        contacts: day.contacts,
        rooming: day.rooming,
        accommodations: day.accommodations,
        distances: day.distances,
        // BAND AND CREW de ce jour : les affectations de la date nommées par le
        // registre. Contrat nom/position partagé (src/lib/people.ts).
        // La sélection du show fait foi ; le rooming n'est qu'un repli (2026-11-03).
        people: (day.people && day.people.length > 0)
          ? day.people
          : crewRowsFromAssignments(day.rooming, day.team),
        // Photo de la salle du jour : même règle que la day sheet seule — pas de
        // photo (ou non résolue) ⇒ pas de section, jamais de cadre vide.
        venuePhoto: day.venue_photo?.url && venuePhotoData?.[day.venue_photo.url]
          ? { dataUrl: venuePhotoData[day.venue_photo.url], caption: (day.venue_photo.caption || '').trim() || day.venue_photo.name || '' }
          : null,
        // Identités pour le rooming et les vols : nom PASSEPORT d'abord.
        members: (day.team || []).map((m) => ({
          id: String(m.id),
          name: personDisplayName(m),
          officialName: personOfficialName(m),
        })),
        bandName: band?.name,
      }, daysheetLayout ?? undefined);
      const dayContent: any[] = dayDoc.content || [];
      // Next-date mention at the end of this daysheet (unless it's the last).
      if (next && sec('next-note')) {
        const n = next.show;
        dayContent.push({
          text: t('pdf.tb.nextLine', { label: T.nextLabel, name: n.name || t('pdf.tb.nextDate'), date: fmtDate(n.date) }),
          margin: [0, 14, 0, 0],
          bold: true,
          fontSize: S.bodyFontSize,
          color: '#1d4e89',
        });
      }
      content.push({ pageBreak: 'before', text: '' }); // start each daysheet on a new page
      content.push(...dayContent);
    });
  }

  const hasFooter = !!(T.footerLeftTemplate || T.footerCenterTemplate || T.footerRightTemplate);
  // Serializable runner spec (see server/routes/pdf.js): pdfmake needs a
  // FUNCTION for pagination and JSON.stringify drops functions, so the
  // client fills what it can and leaves {page}/{pages} for the server.
  const runnerFill = (tpl: string): string =>
    (tpl || '').replace(/\{(\w+)\}/g, (_, k: string) => {
      if (k === 'tourName') return tour.name;
      if (k === 'bandName') return band?.name || '';
      return `{${k}}`; // {page}/{pages} … resolved server-side
    });
  const runnerSpec = (left: string, center: string, right: string, margin: [number, number, number, number]) => ({
    left: runnerFill(left),
    center: runnerFill(center),
    right: runnerFill(right),
    margin,
    fontSize: S.footerFontSize,
    color: S.footerColor,
  });
  const headerMargin: [number, number, number, number] = [S.pageMargins[0], 10, S.pageMargins[2], 0];
  const footerMargin: [number, number, number, number] = [S.pageMargins[0], 0, S.pageMargins[2], 20];

  return {
    content,
    pageSize: 'A4',
    pageMargins: S.pageMargins,
    defaultStyle: { fontSize: S.bodyFontSize, color: S.textColor },
    ...(T.headerEnabled
      ? { headerSpec: runnerSpec(T.headerLeftTemplate, T.headerCenterTemplate, T.headerRightTemplate, headerMargin) }
      : {}),
    // Footer: switchable off explicitly (footerEnabled absent = enabled), on top
    // of the 'at least one template filled' rule.
    ...(hasFooter && T.footerEnabled !== false
      ? { footerSpec: runnerSpec(T.footerLeftTemplate, T.footerCenterTemplate, T.footerRightTemplate, footerMargin) }
      : {}),
  };
}

export async function exportTourBookPdf(input: TourBookInput): Promise<string> {
  const docDef = buildTourBookDoc(input);
  return generatePdfBlobViaServer(docDef);
}
